import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Loader, Lightbulb, ChevronDown, RotateCcw, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { useGetUseCaseQuestionsQuery, useUpdateUseCaseQuestionsMutation, useGetVendorMeQuery } from '../../store/vendorApi';
import { toast } from '@clareefai/ui';

type AnswerType = 'one_selected' | 'many_selected' | 'free_text' | 'numeral' | 'product_select';

interface Choice {
  id: string;
  text: string;
}

interface UseCaseQuestion {
  id: string;
  question: string;
  choices: Choice[];
  answerType: AnswerType;
}

const ANSWER_TYPES: AnswerType[] = ['many_selected', 'one_selected', 'free_text', 'numeral', 'product_select'];

const cid = () => crypto.randomUUID();

const DEFAULT_QUESTIONS = [
  { id: 'd1', question: "What's your role and team size?", choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd2', question: 'Which product/module did you use?', choices: [] as string[], answerType: 'many_selected' as AnswerType },
  { id: 'd3', question: 'What problem were you trying to solve before using our solution?', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd4', question: 'What were you using before (tool/process/vendor), and why did it fall short?', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd5', question: 'What were the top 1–3 reasons you chose us?', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd6', question: 'How did you implement it (time to value, who was involved, any blockers)?', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd7', question: 'Which features did you use most, and for what workflow?', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd8', question: 'What measurable results did you get? (numbers if possible: win rate, sales cycle, hours saved, pipeline influenced, revenue)', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd9', question: 'Can you share one concrete example/story where our solution helped you with a difficult task?', choices: [] as string[], answerType: 'free_text' as AnswerType },
  { id: 'd10', question: 'Who would you recommend us to, and in what situation?', choices: [] as string[], answerType: 'free_text' as AnswerType },
];

const toChoices = (texts: string[]): Choice[] => texts.map(t => ({ id: cid(), text: t }));

const UseCaseQuestionsSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: existingQuestions, isLoading } = useGetUseCaseQuestionsQuery();
  const { data: vendor } = useGetVendorMeQuery();
  const [updateUseCaseQuestions, { isLoading: isSaving }] = useUpdateUseCaseQuestionsMutation();

  const [questions, setQuestions] = useState<UseCaseQuestion[]>([]);
  const seeded = useRef(false);

  useEffect(() => {
    if (existingQuestions === undefined) return;
    if (existingQuestions.length > 0) {
      setQuestions(
        existingQuestions.map((q) => ({
          id: q.id,
          question: q.question,
          choices: toChoices(q.choices || []),
          answerType: (q.answer_type || 'many_selected') as AnswerType,
        }))
      );
    } else if (!seeded.current) {
      seeded.current = true;
      const productTexts = (vendor?.pageInfo?.products || []).map((p: { name: string }) => p.name).filter(Boolean);
      const defaults: UseCaseQuestion[] = DEFAULT_QUESTIONS.map(q => {
        const newId = Date.now().toString() + q.id;
        if (q.id === 'd2' && productTexts.length > 0) {
          return { ...q, id: newId, choices: toChoices(productTexts) };
        }
        return { ...q, id: newId, choices: [] };
      });
      setQuestions(defaults);

      // Auto-save defaults so promoters can see them immediately
      const validDefaults = defaults.filter(q => {
        if (!q.question.trim()) return false;
        if (q.answerType === 'free_text' || q.answerType === 'numeral') return true;
        if (q.answerType === 'product_select') return productTexts.length >= 1;
        return q.choices.length >= 2 && q.choices.every(c => c.text.trim() !== '');
      });
      if (validDefaults.length > 0) {
        const payload = validDefaults.map((q, index) => ({
          question: q.question.trim(),
          choices: q.answerType === 'free_text' ? []
            : q.answerType === 'numeral' ? Array.from({ length: 10 }, (_, i) => String(i + 1))
            : q.answerType === 'product_select' ? productTexts
            : q.choices.filter(c => c.text.trim() !== '').map(c => c.text),
          order: index,
          question_type: 'USE_CASE' as const,
          answer_type: q.answerType,
        }));
        updateUseCaseQuestions({ questions: payload }).unwrap().catch(() => {
          // Silent fail — vendor can still save manually
        });
      }
    }
  }, [existingQuestions, vendor]);

  const addQuestion = () => {
    setQuestions(prev => [
      ...prev,
      { id: cid(), question: '', choices: [], answerType: 'many_selected' },
    ]);
  };

  const updateQuestion = (id: string, text: string) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, question: text } : q));
  };

  const updateAnswerType = (id: string, answerType: AnswerType) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, answerType } : q));
  };

  const deleteQuestion = (id: string) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId.startsWith('choices-')) {
      const questionId = source.droppableId.slice('choices-'.length);
      setQuestions(prev =>
        prev.map(q => {
          if (q.id !== questionId) return q;
          const choices = Array.from(q.choices);
          const [moved] = choices.splice(source.index, 1);
          choices.splice(destination.index, 0, moved);
          return { ...q, choices };
        })
      );
    } else {
      const reordered = Array.from(questions);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      setQuestions(reordered);
    }
  };

  const addChoice = (questionId: string) => {
    setQuestions(prev =>
      prev.map(q => q.id === questionId ? { ...q, choices: [...q.choices, { id: cid(), text: '' }] } : q)
    );
  };

  const updateChoice = (questionId: string, choiceId: string, value: string) => {
    setQuestions(prev =>
      prev.map(q => {
        if (q.id !== questionId) return q;
        return { ...q, choices: q.choices.map(c => c.id === choiceId ? { ...c, text: value } : c) };
      })
    );
  };

  const deleteChoice = (questionId: string, choiceId: string) => {
    setQuestions(prev =>
      prev.map(q => {
        if (q.id !== questionId) return q;
        return { ...q, choices: q.choices.filter(c => c.id !== choiceId) };
      })
    );
  };

  const isValidQuestion = (q: UseCaseQuestion) => {
    if (!q.question.trim()) return false;
    if (q.answerType === 'free_text' || q.answerType === 'numeral') return true;
    if (q.answerType === 'product_select') return (vendor?.pageInfo?.products || []).filter((p: { name: string }) => p.name.trim()).length >= 1;
    return q.choices.length >= 2 && q.choices.every(c => c.text.trim() !== '');
  };

  const hasValidQuestion = useMemo(
    () => questions.some(isValidQuestion),
    [questions]
  );

  const handleSave = async () => {
    const valid = questions.filter(isValidQuestion);

    if (valid.length === 0) return;

    const productNames = (vendor?.pageInfo?.products || []).map((p: { name: string }) => p.name).filter(Boolean);
    const payload = valid.map((q, index) => ({
      question: q.question.trim(),
      choices: q.answerType === 'free_text' ? []
        : q.answerType === 'numeral' ? Array.from({ length: 10 }, (_, i) => String(i + 1))
        : q.answerType === 'product_select' ? productNames
        : q.choices.filter(c => c.text.trim() !== '').map(c => c.text),
      order: index,
      question_type: 'USE_CASE',
      answer_type: q.answerType,
    }));

    try {
      await updateUseCaseQuestions({ questions: payload }).unwrap();
      toast.success(t('settings.useCaseQuestions.saveSuccess'));
    } catch {
      toast.error(t('settings.useCaseQuestions.saveError'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Lightbulb className="w-6 h-6 text-purple-600" />
                <span>{t('settings.useCaseQuestions.heading')}</span>
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {t('settings.useCaseQuestions.subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t('settings.useCaseQuestions.restoreConfirm'))) {
                const productTexts = (vendor?.pageInfo?.products || []).map((p: { name: string }) => p.name).filter(Boolean);
                setQuestions(DEFAULT_QUESTIONS.map(q => {
                  const newId = Date.now().toString() + q.id;
                  if (q.id === 'd2' && productTexts.length > 0) {
                    return { ...q, id: newId, choices: toChoices(productTexts) };
                  }
                  return { ...q, id: newId, choices: [] };
                }));
              }
            }}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            <span>{t('settings.useCaseQuestions.restoreDefaults')}</span>
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="use-case-questions">
              {(droppableProvided) => (
                <div
                  className="space-y-4"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
            {questions.map((q, qIndex) => (
              <Draggable key={q.id} draggableId={q.id} index={qIndex}>
                {(draggableProvided, snapshot) => (
              <div
                ref={draggableProvided.innerRef}
                {...draggableProvided.draggableProps}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${snapshot.isDragging ? 'shadow-lg ring-2 ring-purple-300' : ''}`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div
                    {...draggableProvided.dragHandleProps}
                    className="mt-5 p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing rounded transition-colors"
                    title={t('settings.useCaseQuestions.dragToReorder')}
                  >
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('settings.useCaseQuestions.questionLabel', { number: qIndex + 1 })}
                    </label>
                    <input
                      type="text"
                      value={q.question}
                      onChange={e => updateQuestion(q.id, e.target.value)}
                      placeholder={t('settings.useCaseQuestions.questionPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => deleteQuestion(q.id)}
                    className="mt-5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Answer type selector */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {t('settings.useCaseQuestions.answerType')}
                  </label>
                  <div className="relative inline-block">
                    <select
                      value={q.answerType}
                      onChange={e => updateAnswerType(q.id, e.target.value as AnswerType)}
                      className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                    >
                      {ANSWER_TYPES.map(type => (
                        <option key={type} value={type}>{t(`settings.useCaseQuestions.answerTypes.${type}`)}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Choices / preview based on answer type */}
                {(q.answerType === 'one_selected' || q.answerType === 'many_selected') && (
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t('settings.useCaseQuestions.choices')} <span className="text-red-400">*</span>
                      <span className="ml-1 font-normal normal-case text-gray-400">{t('settings.useCaseQuestions.choicesMin')}</span>
                    </label>
                    <Droppable droppableId={`choices-${q.id}`}>
                      {(choicesProvided) => (
                        <div
                          ref={choicesProvided.innerRef}
                          {...choicesProvided.droppableProps}
                          className="space-y-2"
                        >
                          {q.choices.map((choice, cIndex) => (
                            <Draggable key={choice.id} draggableId={choice.id} index={cIndex}>
                              {(choiceDraggable, choiceSnapshot) => (
                                <div
                                  ref={choiceDraggable.innerRef}
                                  {...choiceDraggable.draggableProps}
                                  className={`flex items-center gap-2 ${choiceSnapshot.isDragging ? 'shadow-md bg-white rounded-lg' : ''}`}
                                >
                                  <div
                                    {...choiceDraggable.dragHandleProps}
                                    className="p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing rounded transition-colors"
                                    title={t('settings.useCaseQuestions.dragToReorder')}
                                  >
                                    <GripVertical className="w-3.5 h-3.5" />
                                  </div>
                                  <input
                                    type="text"
                                    value={choice.text}
                                    onChange={e => updateChoice(q.id, choice.id, e.target.value)}
                                    placeholder={t('settings.useCaseQuestions.choicePlaceholder', { number: cIndex + 1 })}
                                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  />
                                  <button
                                    onClick={() => deleteChoice(q.id, choice.id)}
                                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {choicesProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                    <button
                      onClick={() => addChoice(q.id)}
                      className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700 font-medium mt-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{t('settings.useCaseQuestions.addChoice')}</span>
                    </button>
                  </div>
                )}

                {q.answerType === 'free_text' && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500 italic">
                    {t('settings.useCaseQuestions.freeTextPreview')}
                  </div>
                )}

                {q.answerType === 'numeral' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('settings.useCaseQuestions.scalePreview')}</label>
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(n => (
                        <div key={n} className="w-10 h-10 rounded-lg border-2 border-gray-200 flex items-center justify-center font-semibold text-sm text-gray-500">{n}</div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{t('settings.useCaseQuestions.scaleHelp')}</p>
                  </div>
                )}

                {q.answerType === 'product_select' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('settings.useCaseQuestions.productsLabel')}</label>
                    {(() => {
                      const prods = (vendor?.pageInfo?.products || []).filter((p: { name: string }) => p.name.trim());
                      if (prods.length === 0) return (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                          {t('settings.useCaseQuestions.noProductsConfigured')}
                        </div>
                      );
                      return (
                        <div className="flex flex-wrap gap-2">
                          {prods.map((p: { name: string }) => (
                            <span key={p.name} className="px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">{p.name}</span>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="text-xs text-gray-400 mt-2">{t('settings.useCaseQuestions.productSelectHelp')}</p>
                  </div>
                )}
              </div>
                )}
              </Draggable>
            ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

            {/* Add question */}
            <button
              onClick={addQuestion}
              className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-purple-300 rounded-xl text-purple-600 hover:border-purple-500 hover:bg-purple-50 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              <span>{t('settings.useCaseQuestions.addQuestion')}</span>
            </button>

            {/* Save */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving || !hasValidQuestion}
                className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors"
              >
                {isSaving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>{t('settings.useCaseQuestions.savingEllipsis')}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{t('settings.useCaseQuestions.saveQuestions')}</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UseCaseQuestionsSettings;
